// 9fbef606107a605d69c0edbcd8029e5d
import { SubmissionError } from 'redux-form'; // ES6
import CheckoutConstants from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants';
import getAllCouponList from '@tcp/core/src/queries/getAllCouponList.query.graphql';
import getBonusPoints from '@tcp/core/src/queries/getBonusPoints.query.graphql';
import couponTermsAndConditionQuery from '@tcp/core/src/queries/getCouponTermsAndConditions.query.graphql';
import CacheConfiguration from '@tcp/core/src/services/handler/graphQL/clients/retryConfiguration';
import { executeStatefulAPICall, executeStatefulGraphQLQuery } from '../../handler';
import endpoints from '../../endpoints';
import { getFormattedError, getDynamicCodeErrorMessage } from '../../../utils/errorMessage.util';
import { constructCouponStructure } from './CartItemTile';
import { constructSingleCouponStructure } from '../customerHelp/customerHelp';

export const applyCouponToCart = ({ couponCode = '' }, errorsMapping, cartItemsCount) => {
  if (!cartItemsCount) {
    const errorMessages = {
      _error: {
        msg: errorsMapping.TCP_PROMOTION_COUPON_ON_EMPTY_CART,
      },
    };
    throw new SubmissionError(errorMessages);
  }

  const payload = {
    webService: endpoints.addCoupons,
    body: {
      promoCode: couponCode.trim().toUpperCase(),
    },
  };

  const setPlaceCashError = ({ err: errorObj }) => {
    const placeCash = CheckoutConstants.PLACE_CASH;

    const err = errorObj;
    const isPlaceCashError =
      err.response.body &&
      err.response.body.errors &&
      err.response.body.errors[0].errorParameters &&
      err.response.body.errors[0].errorParameters[1] === placeCash;
    if (isPlaceCashError) {
      err.response.body.errors[0].errorCode = CheckoutConstants.PLACE_CASH_ERROR;
      err.response.body.errors[0].errorKey = CheckoutConstants.PLACE_CASH_ERROR;
    }
    return isPlaceCashError;
  };

  return executeStatefulAPICall(payload)
    .then((res) => {
      return res.body;
    })
    .catch((err) => {
      const isPlaceCashError = setPlaceCashError({ err });
      const error = getFormattedError(err, errorsMapping);
      getDynamicCodeErrorMessage(error, couponCode);

      error.errorMessages = error.errorMessages || {
        _error: 'Oh no! Something went wrong… Please try again in a few minutes.',
      };
      const { errorMessages } = error;
      // eslint-disable-next-line
      errorMessages._error = {
        // eslint-disable-next-line
        msg: errorMessages._error,
        isPlaceCashError,
      };
      throw new SubmissionError(error.errorMessages);
    });
};

export const removeCouponOrPromo = ({ couponCode = '' }) => {
  const payload = {
    header: {
      promoCode: couponCode.toUpperCase(),
    },
    webService: endpoints.removeCouponOrPromo,
  };
  return executeStatefulAPICall(payload).then((res) => {
    const error = getFormattedError(res);
    if (error) {
      return new SubmissionError(error.errorMessages || { _error: 'Oops... an error occured' });
    }
    return { success: true };
  });
};

export const getAllCoupons = (reqParams = {}, deviceSpecificCoupon) => {
  const { sendRedeemedAndExpiredCoupons = false } = reqParams;
  return executeStatefulGraphQLQuery({
    query: getAllCouponList,
    variables: { sendRedeemedAndExpiredCoupons },
  })
    .then((res) => {
      const offers = res?.data?.getAllCoupons?.offers;
      return constructCouponStructure(offers, undefined, deviceSpecificCoupon);
    })
    .catch(() => {
      throw new Error('There is some error in fetching all coupons');
    });
};

export const getBonusPointsDay = (cacheTTL, skipCache = false) => {
  const { preFix, defaultTTL } = CacheConfiguration.GET_BONUS_POINT_DAY;
  return executeStatefulGraphQLQuery({
    query: getBonusPoints,
    cacheConfigKey: `${preFix}`,
    TTL: cacheTTL || defaultTTL,
    fetchPolicy: skipCache ? 'network-only' : '',
  })
    .then((res) => {
      return res?.data?.getBonusDay;
    })
    .catch(() => {
      throw new Error('There is some error in fetching bonus points day');
    });
};

export const getCouponTermsAndConditions = ({ promotionId }) => {
  return executeStatefulGraphQLQuery({
    query: couponTermsAndConditionQuery,
    variables: { promotionId },
  })
    .then((res) => {
      return res?.data?.getCouponTermsAndConditions;
    })
    .catch(() => {
      throw new Error('There is some error in fetching coupon terms and conditions');
    });
};
export const getCouponDetails = ({ couponCode = '' }) => {
  const payload = {
    webService: endpoints.getCouponDetailsCsh,
    header: {
      fromcsh: true,
    },
    body: {
      couponCode: [couponCode.trim().toUpperCase()],
    },
  };
  return executeStatefulAPICall(payload).then((res) => {
    const couponDetail = res?.body?.data?.CouponDetails[0];
    if (couponDetail) {
      if (couponDetail?.errorCode) {
        throw new Error(couponDetail.errorCode, { cause: couponDetail });
      }
      return constructSingleCouponStructure(couponDetail);
    }
    throw new Error('There is some error in fetching coupon details');
  });
};

export default {
  applyCouponToCart,
  removeCouponOrPromo,
  getAllCoupons,
  getCouponTermsAndConditions,
  getCouponDetails,
};
